import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { absenceApi, Absence } from '../../services/employeeService';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { NoStaffProfileState } from './EmployeeTimePanel';
import { tokens } from '../../theme';
import { countLabel, formatDate } from '../../utils/format';

const TYPE_LABEL: Record<string, string> = {
  SICK_LEAVE: 'L4', VACATION: 'Urlop', EXCUSED: 'Usprawiedliwiona', UNEXCUSED: 'Nieusprawiedliwiona', OTHER: 'Inne',
};

// Pelne nazwy do tabeli i formularza — skroty z TYPE_LABEL bywaja niejasne bez kontekstu.
const TYPE_FULL_LABEL: Record<string, string> = {
  SICK_LEAVE: 'L4 — zwolnienie lekarskie',
  VACATION: 'Urlop',
  EXCUSED: 'Nieobecność usprawiedliwiona',
  UNEXCUSED: 'Nieobecność nieusprawiedliwiona',
  OTHER: 'Inna nieobecność',
};

const STATUS_META: Record<string, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Czeka na decyzję', tone: 'warning' },
  APPROVED: { label: 'Zaakceptowany', tone: 'success' },
  REJECTED: { label: 'Odrzucony', tone: 'danger' },
  NEEDS_INFO: { label: 'Wymaga uzupełnienia', tone: 'info' },
};

const DAY_MS = 24 * 60 * 60 * 1000;
const daysInclusive = (from: string, to: string) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / DAY_MS) + 1);
};

const apiStatus = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

const EMPTY_FORM = { type: 'VACATION', startDate: '', endDate: '', note: '' };

export const MyAbsencesPage: React.FC = () => {
  const [rows, setRows] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<'none' | 'noProfile' | 'failed'>('none');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await absenceApi.mine());
      setLoadError('none');
    } catch (e) {
      setLoadError(apiStatus(e) === 404 ? 'noProfile' : 'failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openForm = () => { setTriedSubmit(false); setOpen(true); };

  const submit = async () => {
    if (!form.startDate || !form.endDate) { setTriedSubmit(true); return; }
    try {
      await absenceApi.create(form);
      setOpen(false); setForm(EMPTY_FORM); setTriedSubmit(false);
      setToast({ msg: 'Wniosek wysłany do przełożonego', sev: 'success' }); load();
    } catch (e) {
      setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się wysłać wniosku. Spróbuj ponownie.', sev: 'error' });
    }
  };

  const pending = rows.filter((r) => r.status === 'PENDING').length;
  const rangeInvalid = Boolean(form.startDate && form.endDate && form.endDate < form.startDate);

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Moje sprawy' }]}
        title="Moje nieobecności"
        description={rows.length
          ? `${countLabel(rows.length, 'wniosek', 'wnioski', 'wniosków')}${pending ? ` · ${countLabel(pending, 'czeka', 'czekają', 'czeka')} na decyzję` : ''}`
          : 'Urlopy, L4 i inne nieobecności — złóż wniosek i sprawdź decyzję przełożonego.'}
        actions={loadError === 'noProfile' ? undefined : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openForm}>Złóż wniosek</Button>
        )}
      />

      {loading ? (
        <LoadingState variant="table" rows={3} />
      ) : loadError === 'noProfile' ? (
        <NoStaffProfileState lead="Wnioski o urlop i L4 składają osoby dodane do zespołu salonu." />
      ) : loadError === 'failed' ? (
        <SectionCard>
          <EmptyState
            icon={<ErrorOutlineIcon />}
            title="Nie udało się wczytać wniosków"
            description="Sprawdź połączenie z internetem i spróbuj ponownie."
            actions={<Button variant="outlined" onClick={load}>Spróbuj ponownie</Button>}
          />
        </SectionCard>
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<EventBusyOutlinedIcon />}
            title="Nie masz jeszcze żadnych wniosków"
            description="Złóż wniosek o urlop albo zgłoś L4 — przełożony zobaczy go w swoim panelu."
            actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openForm}>Złóż wniosek</Button>}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Moje wnioski" padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Rodzaj</TableCell>
                  <TableCell>Od</TableCell>
                  <TableCell>Do</TableCell>
                  <TableCell align="right">Dni</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const st = STATUS_META[r.status] || { label: r.status, tone: 'neutral' as PillTone };
                  const days = daysInclusive(r.startDate, r.endDate);
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{TYPE_FULL_LABEL[r.type] || TYPE_LABEL[r.type] || r.type}</Typography>
                        {r.note && (
                          <Typography variant="caption" component="div" sx={{ color: tokens.muted, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(r.startDate)}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(r.endDate)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{days ?? '—'}</TableCell>
                      <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="absence-form-title">
        <DialogTitle id="absence-form-title">Złóż wniosek o nieobecność</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <TextField
              select
              label="Rodzaj nieobecności"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            >
              {Object.keys(TYPE_LABEL).map((k) => <MenuItem key={k} value={k}>{TYPE_FULL_LABEL[k]}</MenuItem>)}
            </TextField>
            <TextField
              label="Od"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              error={triedSubmit && !form.startDate}
              helperText={triedSubmit && !form.startDate ? 'Podaj pierwszy dzień nieobecności' : undefined}
              fullWidth
              sx={{ minWidth: 170 }}
            />
            <TextField
              label="Do"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              error={(triedSubmit && !form.endDate) || rangeInvalid}
              helperText={triedSubmit && !form.endDate
                ? 'Podaj ostatni dzień nieobecności'
                : rangeInvalid ? 'Data końca jest wcześniejsza niż początku' : undefined}
              inputProps={{ min: form.startDate || undefined }}
              fullWidth
              sx={{ minWidth: 170 }}
            />
            <TextField
              label="Notatka (opcjonalnie)"
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              helperText="Np. osoba zastępująca albo numer zaświadczenia."
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={submit}>Wyślij wniosek</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default MyAbsencesPage;
